<script setup lang="ts">
    import { onMounted, onUnmounted, useTemplateRef, watch, useSlots } from 'vue';
    import { useRouter } from 'vue-router';
    import { useScreenOrientation } from '@vueuse/core';
    import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

    const {
        isOpen,
        closeOnRouteChange = true,
        closeButton = true,
        fullScreen = false,
        requestOrientation = null
    } = defineProps<{
        isOpen: boolean;
        closeOnRouteChange?: boolean;
        closeButton?: boolean;
        fullScreen?: boolean;
        requestOrientation?: 'landscape' | 'portrait' | null;
    }>();

    const emit = defineEmits<{ (e: 'close'): void }>();

    const displayOrientationHint = ref(false);

    let animationFrameId: number | null = null;
    let clampedBody = false;

    const { clampBody, releaseBody } = useFixedBody();

    const router = useRouter();
    const { orientation } = useScreenOrientation();
    const slots = useSlots();

    const trapTarget = useTemplateRef<HTMLDivElement>('trapTarget');
    const { activate, deactivate } = useFocusTrap(trapTarget, {
        allowOutsideClick: true
    });

    const clampPage = () => {
        clampBody();
        clampedBody = true;
    };

    const releasePage = () => {
        if (!clampedBody) return;

        releaseBody();
        clampedBody = false;
    };

    watch(
        orientation,
        (orientation) => {
            if (requestOrientation && !orientation?.startsWith(requestOrientation)) {
                displayOrientationHint.value = true;
            } else {
                displayOrientationHint.value = false;
            }
        },
        { immediate: true }
    );

    watch(
        () => isOpen,
        (open) => {
            if (open) {
                clampPage();
                animationFrameId = requestAnimationFrame(() => {
                    animationFrameId = null;
                    activate();
                });
            } else {
                if (animationFrameId != null) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }
                releasePage();
                deactivate();
            }
        },
        { immediate: true }
    );

    watch(
        () => router.currentRoute.value.path,
        () => {
            if (isOpen && closeOnRouteChange) {
                emit('close');
            }
        }
    );

    onMounted(() => {
        document.addEventListener('keydown', handleKeyDownEsc);
    });

    onUnmounted(() => {
        document.removeEventListener('keydown', handleKeyDownEsc);
        if (animationFrameId != null) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }
        deactivate();
        releasePage();
    });

    function handleClickOutside(e: MouseEvent) {
        if (!isOpen) return;
        e.stopPropagation();
        e.preventDefault();
        emit('close');
    }

    function handleKeyDownEsc(e: KeyboardEvent) {
        if (!isOpen) return;
        if (e.key === 'Escape') {
            emit('close');
        }
    }
</script>

<template>
    <!-- teleport to body so a transformed or clipping ancestor (e.g. a splide track) cannot
         become the containing block of the fixed backdrop -->
    <Teleport to="body">
        <Transition name="fade-in-out">
            <div
                v-if="isOpen"
                class="backdrop"
                :class="{
                    'dialog-open': isOpen,
                    'full-screen': fullScreen
                }"
                role="dialog"
                aria-modal="true"
                v-bind="$attrs"
                @click="handleClickOutside"
            >
                <div
                    ref="trapTarget"
                    class="wrapper"
                    v-bind="$attrs"
                    @click.stop
                >
                    <div class="modal-content">
                        <div
                            v-if="slots.header"
                            class="modal-content-header"
                        >
                            <slot name="header" />
                        </div>
                        <div class="modal-content-body">
                            <slot
                                name="content"
                                @close="$emit('close')"
                            />
                        </div>
                        <div
                            v-if="slots.footer"
                            class="modal-content-footer"
                        >
                            <slot
                                name="footer"
                                @close="$emit('close')"
                            />
                        </div>
                        <!-- use v-show here instead of v-if to avoid missing icon -->
                        <div
                            v-show="displayOrientationHint"
                            class="orientation-hint"
                        >
                            <div class="orientation-hint-content">
                                <Icon
                                    name="hfp:tilt-phone"
                                    :size="80"
                                />
                                <p>{{ $t('general.modal.orientationHint') }}</p>
                            </div>
                        </div>

                        <button
                            v-if="closeButton"
                            type="button"
                            class="close-button"
                            @click="$emit('close')"
                            @keydown.space="$emit('close')"
                        >
                            <span class="sr-only">{{ $t('general.close') }}</span>
                            <img
                                src="~/assets/icons/close-white.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style lang="scss" scoped>
    .backdrop {
        position: fixed;
        inset: 0;
        background-color: $gray-transparent;
        z-index: 50;
        padding: $spacing;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .orientation-hint {
        position: absolute;
        inset: 0;
        z-index: 1;
        background-color: $gray-transparent;
        backdrop-filter: blur(10px);
        color: var(--white);

        &-content {
            position: absolute;
            top: calc(100% / 2.618);
            left: 50%;
            display: flex;
            flex-direction: column;
            align-items: center;
            transform: translate(-50%, -50%);
            width: 100%;
        }

        span,
        p {
            color: var(--white);
            font-weight: 700;
            font-size: var(--font-m);
            line-height: 1;
            letter-spacing: 0;
            text-align: center;
            margin: var(--spacing--2);
            text-rendering: optimizeLegibility;
        }
    }

    .full-screen {
        &.backdrop {
            padding: 0;
        }

        .wrapper {
            max-width: 100dvw;
            max-height: 100dvh;
            width: 100dvw;
            height: 100dvh;
            border-radius: 0;
        }

        @media (min-width: $br-m) {
            .wrapper {
                max-width: calc(100dvw - $spacing--2);
                max-height: calc(100dvh - $spacing--2);
                width: calc(100dvw - $spacing--2);
                height: calc(100dvh - $spacing--2);
                border-radius: $border-radius;
            }
        }
    }

    .wrapper {
        background-color: $white;
        border-radius: $border-radius;
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 600px;
        max-height: 100%;
        overflow: hidden;
    }

    .close-button {
        position: absolute;
        z-index: 2;
        right: $spacing;
        top: $spacing--05;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: auto;
        padding: $spacing--05;
        background-color: var(--header-mobile-button-color, $black);
        border-radius: 100%;
        cursor: pointer;
        transition:
            background-color $anim-duration-hover $anim-ease,
            transform $anim-duration-hover $anim-ease-bounce;

        &:hover {
            background-color: $color-black-hover;
            transform: $anim-scale-hover;
        }
    }

    .modal-content {
        position: relative;
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;

        .modal-content-body {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
        }

        .modal-content-footer {
            flex: none;
            display: flex;
            padding: $spacing;
        }

        .modal-content-header {
            min-height: 0;
            flex: none;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: $spacing--05;
            padding: $spacing;
        }
    }

    .fade-in-out-enter-active,
    .fade-in-out-leave-active {
        transition: opacity 0.3s ease-in-out;
    }

    .fade-in-out-enter-from,
    .fade-in-out-leave-to {
        opacity: 0;
    }
</style>
